package org.zq.flvtomp4.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.zq.flvtomp4.converter.ConvertMode
import org.zq.flvtomp4.converter.createVideoConverter
import org.zq.flvtomp4.platform.PlatformFiles
import org.zq.flvtomp4.queue.AppSettings
import org.zq.flvtomp4.queue.ConvertQueueManager
import org.zq.flvtomp4.queue.ConvertTask
import org.zq.flvtomp4.queue.OutputLocation
import org.zq.flvtomp4.queue.OverwritePolicy
import org.zq.flvtomp4.queue.TaskStatus

data class MainUiState(
    val tasks: List<ConvertTask> = emptyList(),
    val settings: AppSettings = AppSettings(),
    val completedCount: Int = 0,
    val totalCount: Int = 0,
    val currentSpeed: String? = null,
    val remainingLabel: String? = null,
    val isRunning: Boolean = false,
    val selectedTab: AppTab = AppTab.CONVERT,
)

enum class AppTab {
    CONVERT,
    SETTINGS,
    HISTORY,
    ABOUT,
}

class MainViewModel : ViewModel() {

    private val queue = ConvertQueueManager(
        converterFactory = { settings ->
            createVideoConverter(
                ffmpegPath = settings.ffmpegPath.ifBlank { null },
                ffprobePath = settings.ffprobePath.ifBlank { null },
            )
        },
        scope = viewModelScope,
    )

    private val _settings = MutableStateFlow(AppSettings())
    private val _selectedTab = MutableStateFlow(AppTab.CONVERT)
    private val _history = MutableStateFlow<List<ConvertTask>>(emptyList())

    val uiState: StateFlow<MainUiState> = combine(
        queue.tasks,
        _settings,
        _selectedTab,
    ) { tasks, settings, tab ->
        val (done, total) = queue.overallProgress()
        val remainingMs = queue.estimatedRemainingMs()
        MainUiState(
            tasks = tasks,
            settings = settings,
            completedCount = done,
            totalCount = total,
            currentSpeed = queue.currentSpeed(),
            remainingLabel = remainingMs?.let { formatDuration(it) },
            isRunning = tasks.any {
                it.status == TaskStatus.CONVERTING || it.status == TaskStatus.ANALYZING
            },
            selectedTab = tab,
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = MainUiState(),
    )

    val history: StateFlow<List<ConvertTask>> = _history.asStateFlow()

    init {
        viewModelScope.launch {
            queue.tasks.collect { tasks ->
                val finished = tasks.filter { it.status == TaskStatus.COMPLETED }
                if (finished.isNotEmpty()) {
                    _history.updateUnique(finished)
                }
            }
        }
    }

    fun selectTab(tab: AppTab) {
        _selectedTab.value = tab
    }

    fun addFiles(paths: List<String>) {
        viewModelScope.launch {
            queue.addFiles(paths)
        }
    }

    fun pickAndAddFiles() {
        viewModelScope.launch {
            val files = PlatformFiles.pickFlvFiles()
            if (files.isNotEmpty()) {
                queue.addFiles(files)
            }
        }
    }

    fun startAll() {
        queue.startAll()
    }

    fun cancelTask(taskId: String) {
        queue.cancelTask(taskId)
    }

    fun removeTask(taskId: String) {
        queue.removeTask(taskId)
    }

    fun clearCompleted() {
        queue.clearCompleted()
    }

    fun openOutput(task: ConvertTask) {
        val dir = PlatformFiles.parent(task.outputPath) ?: return
        PlatformFiles.openDirectory(dir)
    }

    fun openOutputFolder() {
        val settings = _settings.value
        val dir = when (settings.outputLocation) {
            OutputLocation.CUSTOM -> settings.customOutputDir
            OutputLocation.SAME_AS_SOURCE -> {
                uiState.value.tasks.firstOrNull()?.let {
                    PlatformFiles.parent(it.outputPath)
                }
            }
        } ?: return
        PlatformFiles.openDirectory(dir)
    }

    fun updateMode(mode: ConvertMode) {
        updateSettings { copy(mode = mode) }
    }

    fun updateQuality(quality: Int) {
        updateSettings { copy(quality = quality.coerceIn(18, 28)) }
    }

    fun updateOutputLocation(location: OutputLocation) {
        updateSettings { copy(outputLocation = location) }
    }

    fun pickOutputDirectory() {
        val dir = PlatformFiles.pickDirectory() ?: return
        updateSettings {
            copy(
                outputLocation = OutputLocation.CUSTOM,
                customOutputDir = dir,
            )
        }
    }

    fun updateOverwritePolicy(policy: OverwritePolicy) {
        updateSettings { copy(overwritePolicy = policy) }
    }

    fun updateConcurrency(remux: Int, transcode: Int) {
        updateSettings {
            copy(
                maxConcurrentRemux = remux.coerceIn(1, 8),
                maxConcurrentTranscode = transcode.coerceIn(1, 4),
            )
        }
    }

    fun pickFfmpeg() {
        val path = PlatformFiles.pickExecutable("选择 ffmpeg 可执行文件") ?: return
        updateSettings { copy(ffmpegPath = path) }
    }

    fun pickFfprobe() {
        val path = PlatformFiles.pickExecutable("选择 ffprobe 可执行文件") ?: return
        updateSettings { copy(ffprobePath = path) }
    }

    fun clearFfmpegPaths() {
        updateSettings { copy(ffmpegPath = "", ffprobePath = "") }
    }

    private fun updateSettings(transform: AppSettings.() -> AppSettings) {
        val next = _settings.value.transform()
        _settings.value = next
        queue.updateSettings(next)
    }

    private fun formatDuration(ms: Long): String {
        val totalSeconds = (ms / 1000).coerceAtLeast(0)
        val hours = totalSeconds / 3600
        val minutes = (totalSeconds % 3600) / 60
        val seconds = totalSeconds % 60
        fun pad(value: Long): String = value.toString().padStart(2, '0')
        return if (hours > 0) {
            "${pad(hours)}:${pad(minutes)}:${pad(seconds)}"
        } else {
            "${pad(minutes)}:${pad(seconds)}"
        }
    }
}

private fun MutableStateFlow<List<ConvertTask>>.updateUnique(finished: List<ConvertTask>) {
    val current = value.associateBy { it.id }.toMutableMap()
    finished.forEach { task -> current[task.id] = task }
    value = current.values
        .sortedByDescending { it.id }
        .take(100)
}
